// Thin event-helper layer over PostHog. Keep events few, named, and stable so
// the funnel report on PostHog stays clean. Adding properties is fine; renaming
// or removing events is a breaking change for whatever dashboard exists.

import { posthog, isAnalyticsInitialized } from '@/lib/analytics'
import { sanitizeTrackedPath } from '@/lib/trackedPath'

type EventProps = Record<string, string | number | boolean | undefined>

function safeCapture(event: string, props?: EventProps) {
  if (!isAnalyticsInitialized()) return
  try {
    posthog.capture(event, props)
  } catch (err) {
    // Analytics must never break product flows.
    console.warn('[analytics] capture failed', err)
  }
}

export function useAnalytics() {
  return {
    trackPageView(path: string, properties?: EventProps) {
      // Emailed links carry tokens (reset / invite / unsubscribe): they never leave the browser.
      const safe = sanitizeTrackedPath(path)
      safeCapture('$pageview', { $current_url: safe, path: safe, ...properties })
    },

    trackDemoStart(properties?: EventProps) {
      safeCapture('demo_started', properties)
    },

    trackDemoSession30s(properties?: EventProps) {
      safeCapture('demo_session_30s', properties)
    },

    trackSignupStart(source?: string) {
      safeCapture('signup_started', { source })
    },

    trackTrialStart(properties?: EventProps) {
      safeCapture('trial_started', properties)
    },

    trackFirstDealCreated(properties?: EventProps) {
      safeCapture('first_deal_created', properties)
    },

    trackFirstCallLogged(properties?: EventProps) {
      safeCapture('first_call_logged', properties)
    },

    trackSubscriptionStarted(properties?: EventProps) {
      safeCapture('subscription_started', properties)
    },

    trackNewsletterSignup(properties?: EventProps) {
      safeCapture('newsletter_signup', properties)
    },

    /** Identify a known user once auth resolves. Safe to call repeatedly. */
    identify(userId: string, traits?: EventProps) {
      if (!isAnalyticsInitialized()) return
      try {
        posthog.identify(userId, traits as Record<string, unknown>)
      } catch (err) {
        console.warn('[analytics] identify failed', err)
      }
    },

    reset() {
      if (!isAnalyticsInitialized()) return
      try {
        posthog.reset()
      } catch (err) {
        console.warn('[analytics] reset failed', err)
      }
    },
  }
}
